// Shows a client's ink signature on file. The image endpoint is Bearer-guarded,
// so it is fetched as a blob and shown through an object URL (revoked on unmount).
// The object URL is stored TOGETHER with the client id it belongs to, and is only
// rendered while the props still name that same client — so a re-render with a new
// clientId can never show the previous client's signature, even for one frame.
import { useEffect, useState } from "react";
import { clients as clientsApi } from "../lib/api";

type Loaded = { clientId: string; version: string | null | undefined; url: string | null; state: "ok" | "none" | "error" };

export function ClientSignatureImg({ clientId, version, className, alt = "Signature on file" }: {
  clientId: string;
  /** Bump to refetch (e.g. the signature id) */
  version?: string | null;
  className?: string;
  alt?: string;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    clientsApi.getSignatureImageUrl(clientId)
      .then((u) => {
        if (!alive) { if (u) URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setLoaded({ clientId, version, url: u, state: u ? "ok" : "none" });
      })
      .catch(() => { if (alive) setLoaded({ clientId, version, url: null, state: "error" }); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [clientId, version]);
  const current = loaded && loaded.clientId === clientId && loaded.version === version ? loaded : null;
  if (!current) return <span className="muted small">Loading signature…</span>;
  if (current.state === "none") return <span className="muted small">No signature on file.</span>;
  if (current.state === "error" || !current.url) return <span className="muted small">Signature could not be loaded.</span>;
  return <img src={current.url} alt={alt} className={className} />;
}
